import {
    ActivityType,
    NewsActivity,
    Question,
    Survey,
    SurveyTaskActivity,
    Task,
    TaskActivity,
    VisitActivity,
    VisitOverlapTimeData,
} from '@/app/models/new-activity-creation-state'
import { newsForm } from '@/app/models/new-activity-news-form'
import {
    NewActivitySectionType,
    newsSectionMap,
    surveyTaskSectionMap,
    taskSectionMap,
    visitSectionMap,
} from '@/app/models/new-activity-section-type'
import { surveyTaskForm } from '@/app/models/new-activity-survey-task-form'
import { taskCommunicationForm } from '@/app/models/new-activity-task-communication-form'
import { visitForm } from '@/app/models/new-activity-visit-form'
import { ProjectApiVisibility } from '@/app/models/projects-service'
import { LayoutActivityType } from '@Techedge/digitalportal-shared'
import { NewActivityCreationActions } from '../actions/activity-creation'

export const NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE = 'newActivityCreationReducer'
export const GALLERY_REDUCER_NAMESPACE = 'galleryReducer'

export interface NewActivityState {
    activityTypeList: ActivityType[]
    activeActivity?: LayoutActivityType
    newsActivity: NewsActivity
    taskActivity: TaskActivity
    surveyTaskActivity: SurveyTaskActivity
    visitActivity: VisitActivity
    projectVisibilityOptions: { value: string; label: string }[]
    questionList: Question[]
    taskList: Task[]
    surveyList: Survey[]
    ctaTemplate: any
    banner: string[]
    region: string[]
    country: string[]
    isEdit: boolean
    changelog: Record<string, string>
    isAllStoreSelected: boolean
    changeFields: any[]
    isResetMandatory: boolean
    schedulingMinStartDate: number
    schedulingMaxEndDate: number
    smartFilter: any
    visitOverlapTimeData: VisitOverlapTimeData
    isActivityTypeStep: boolean
    selectedActivityType: Partial<ActivityType>
    currentStep: NewActivitySectionType
}

const defaultState: NewActivityState = {
    banner: [],
    region: [],
    country: [],
    activityTypeList: [],
    activeActivity: LayoutActivityType.TASK,
    newsActivity: {
        typeList: [],
        sectionMap: newsSectionMap,
        form: { ...newsForm },
    },
    questionList: [],
    taskList: [],
    surveyList: [],
    taskActivity: {
        typeList: [],
        sectionMap: taskSectionMap,
        form: { ...taskCommunicationForm },
    },
    surveyTaskActivity: {
        sectionMap: surveyTaskSectionMap,
        form: { ...surveyTaskForm },
    },
    visitActivity: {
        sectionMap: visitSectionMap,
        form: { ...visitForm },
        selectedSurveys: [],
        surveyList: [],
        surveyListLoading: false,
    },
    projectVisibilityOptions: [
        { value: ProjectApiVisibility.IMMEDIATE, label: 'immediate' },
        { value: ProjectApiVisibility.OVERNIGHT, label: 'overnight' },
        { value: ProjectApiVisibility.DAYS_BEFORE_START_DATE, label: 'days.before.start.date' },
        { value: ProjectApiVisibility.SPECIFIC_DATE, label: 'specific.date' },
    ],
    ctaTemplate: null,
    isEdit: false,
    changelog: {},
    isAllStoreSelected: false,
    changeFields: [],
    isResetMandatory: false,
    schedulingMaxEndDate: null,
    schedulingMinStartDate: null,
    smartFilter: null,
    visitOverlapTimeData: null,
    isActivityTypeStep: false,
    selectedActivityType: null,
    currentStep: null
}

export default (state = defaultState, action: any) => {
    let newQuestionList,
        newQuestion,
        newAnswerList,
        newAnswer,
        newTaskList,
        newTask,
        newSurveyList,
        newForm,
        newActivity,
        newChangelog
    switch (action.type) {
        case NewActivityCreationActions.SET_FILTERS_DOMAIN:
            return {
                ...state,
                banner: [...action.payload.banner],
                region: [...action.payload.region],
                country: [...action.payload.country],
            }
        case NewActivityCreationActions.SET_ACTIVITY_KEY:
            return {
                ...state,
                activeActivity: action.payload.key,
            }
        case NewActivityCreationActions.SET_ACTIVITY_STATUS:
            const newList = [...state.activityTypeList]
            const newActiveTypeList = newList.map((activity) => ({
                ...activity,
                active: activity.key === action.payload.key,
            }))
            let activeActivity = newActiveTypeList.find((activity) => activity.active)
            return {
                ...state,
                ...(action.payload.disableReset ? {} : defaultState),
                activityTypeList: [...newActiveTypeList],
                activeActivity: activeActivity?.key || LayoutActivityType.TASK,
            }
        case NewActivityCreationActions.SET_ACTIVITY_SECTION_STATUS:
            newActivity = { ...state[action.payload.activityKey] }
            let newSectionMap = { ...newActivity.sectionMap }
            newSectionMap[action.payload.sectionKey] = {
                ...newSectionMap[action.payload.sectionKey],
                ...action.payload.section,
            }
            newActivity.sectionMap = { ...newSectionMap }
            state[action.payload.activityKey] = {
                ...[action.payload.activityKey],
                ...newActivity,
            }
            return {
                ...state,
            }
        case NewActivityCreationActions.SET_ACTIVITY_TYPE:
            return {
                ...state,
                activityTypeList: [...action.payload.activityList],
            }
        case NewActivityCreationActions.SET_ACTIVITY_TYPE_LIST:
            newActivity = { ...state[action.payload.activityKey] }
            newActivity.typeList = [...action.payload.typeList]
            state[action.payload.activityKey] = { ...newActivity }
            return {
                ...state,
            }
        case NewActivityCreationActions.UPDATE_ANSWER_LIST:
            newQuestionList = [...state.questionList]
            newQuestion = { ...newQuestionList[action.payload.questionIndex] }
            newAnswerList = [...newQuestion.answerList]
            newAnswerList[action.payload.index] = {
                ...newAnswerList[action.payload.index],
                value: action.payload.value,
                disabled: action.payload.disabled,
            }
            newQuestion = {
                ...newQuestion,
                answerList: [...newAnswerList],
            }
            newQuestionList[action.payload.questionIndex] = { ...newQuestion }
            newQuestionList = [...newQuestionList]
            return {
                ...state,
                questionList: [...newQuestionList],
            }
        case NewActivityCreationActions.REMOVE_ANSWER:
            newQuestionList = [...state.questionList]
            newQuestion = { ...newQuestionList[action.payload.questionIndex] }
            newAnswerList = [...newQuestion.answerList]
            newAnswerList.splice(action.payload.answerIndex, 1)
            newQuestion = {
                ...newQuestion,
                answerList: [...newAnswerList],
            }
            newQuestionList[action.payload.questionIndex] = { ...newQuestion }
            newQuestionList = [...newQuestionList]
            return {
                ...state,
                questionList: [...newQuestionList],
            }
        case NewActivityCreationActions.REMOVE_QUESTION:
            newQuestionList = [...state.questionList]
            newQuestionList.splice(action.index, 1)
            return {
                ...state,
                questionList: [...newQuestionList],
            }
        case NewActivityCreationActions.SET_QUESTION_STATUS:
            newQuestionList = [...state.questionList]
            newQuestion = {
                ...newQuestionList[action.payload.index],
                ...action.payload.question,
            }
            newQuestionList[action.payload.index] = { ...newQuestion }
            return {
                ...state,
                questionList: [...newQuestionList],
            }
        case NewActivityCreationActions.SET_TASK_STATUS:
            newTaskList = [...state.taskList]
            newTaskList[action.payload.index] = {
                ...newTaskList[action.payload.index],
                ...action.payload.task,
            }
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.SET_TASK_QUESTION_STATUS:
            newTaskList = [...state.taskList]
            newTask = {
                ...newTaskList[action.payload.taskIndex],
            }
            newQuestionList = [...newTask.questionList]
            newQuestion = {
                ...newTask.questionList[action.payload.questionIndex],
                ...action.payload.question,
            }
            newQuestionList[action.payload.questionIndex] = { ...newQuestion }
            newTask = {
                ...newTask,
                questionList: [...newQuestionList],
            }
            newTaskList[action.payload.taskIndex] = { ...newTask }
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.REMOVE_TASK_QUESTION:
            newTaskList = [...state.taskList]
            newTask = {
                ...newTaskList[action.payload.taskIndex],
            }
            newQuestionList = [...newTask.questionList]
            newQuestionList.splice(action.payload.questionIndex, 1)
            newTask.questionList = [...newQuestionList]
            newTaskList[action.payload.taskIndex] = { ...newTask }
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.UPDATE_TASK_QUESTION_ANSWER_LIST:
            newTaskList = [...state.taskList]
            newTask = {
                ...newTaskList[action.payload.taskIndex],
            }
            newQuestionList = [...newTask.questionList]
            newQuestion = {
                ...newTask.questionList[action.payload.questionIndex],
                ...action.payload.question,
            }
            newAnswerList = [...newQuestion.answerList]
            newAnswer = {
                ...newAnswerList[action.payload.answerIndex],
                value: action.payload.value,
                disabled: action.payload.disabled,
            }
            newAnswerList[action.payload.answerIndex] = { ...newAnswer }
            newQuestion.answerList = [...newAnswerList]
            newQuestionList[action.payload.questionIndex] = { ...newQuestion }
            newTask.questionList = [...newQuestionList]
            newTaskList[action.payload.taskIndex] = { ...newTask }
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.REMOVE_TASK_QUESTION_ANSWER:
            newTaskList = [...state.taskList]
            newTask = {
                ...newTaskList[action.payload.taskIndex],
            }
            newQuestionList = [...newTask.questionList]
            newQuestion = {
                ...newTask.questionList[action.payload.questionIndex],
                ...action.payload.question,
            }
            newAnswerList = [...newQuestion.answerList]
            newAnswerList.splice(action.payload.answerIndex, 1)
            newQuestion.answerList = [...newAnswerList]
            newQuestionList[action.payload.questionIndex] = { ...newQuestion }
            newTask.questionList = [...newQuestionList]
            newTaskList[action.payload.taskIndex] = { ...newTask }
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.REMOVE_TASK:
            newTaskList = [...state.taskList]
            newTaskList.splice(action.index, 1)
            return {
                ...state,
                taskList: [...newTaskList],
            }
        case NewActivityCreationActions.SET_SURVEY_STATUS:
            newSurveyList = [...state.surveyList]
            newSurveyList[action.payload.index] = {
                ...newSurveyList[action.payload.index],
                ...action.payload.survey,
            }
            return {
                ...state,
                surveyList: [...newSurveyList],
            }
        case NewActivityCreationActions.REMOVE_SURVEY:
            newSurveyList = [...state.surveyList]
            newSurveyList.splice(action.index, 1)
            return {
                ...state,
                surveyList: [...newSurveyList],
            }
        case NewActivityCreationActions.SET_NEW_ACTIVITY_FORM:
            newActivity = { ...state[action.payload.activityName] }
            newForm = { ...newActivity.form }
            let newsSectionForm = { ...newForm[action.payload.section], ...action.payload.form }
            newForm[action.payload.section] = { ...newsSectionForm }
            newActivity.form = { ...newForm }
            state[action.payload.activityName] = { ...newActivity }
            return {
                ...state,
            }
        case NewActivityCreationActions.RESET_FORM:
            newActivity = { ...state[action.payload.activityName] }
            newActivity.form = { ...action.payload.form }
            state[action.payload.activityName] = { ...newActivity }
            // only this field not reset with previous row assignment
            state.taskActivity.form.distribution.selectedDistributionList.value = ''
            return {
                ...state,
            }
        case NewActivityCreationActions.RESET_ACTION_LIST:
            state[action.listName] = []
            return {
                ...state,
            }
        case NewActivityCreationActions.SET_CTA_TEMPLATE:
            return {
                ...state,
                ctaTemplate: action.template,
            }
        case NewActivityCreationActions.REMOVE_CHANGELOG:
            newChangelog = { ...state.changelog }
            delete newChangelog[action.field]
            return {
                ...state,
                changelog: { ...newChangelog },
            }
        case NewActivityCreationActions.ADD_CHANGELOG:
            return {
                ...state,
                changelog: { ...state.changelog, ...action.changelog },
            }
        case NewActivityCreationActions.SET_IS_ALL_STORE_SELECTED:
            return {
                ...state,
                isAllStoreSelected: action.value,
            }
        case NewActivityCreationActions.SET_CHANGE_FIELDS:
            return {
                ...state,
                changeFields: [...action.value],
            }
        case NewActivityCreationActions.SET_RESET_MANDATORY:
            return {
                ...state,
                isResetMandatory: action.value,
            }
        case NewActivityCreationActions.SET_SCHEDULING_MIN_START_DATE:
            return {
                ...state,
                schedulingMinStartDate: action.value,
            }
        case NewActivityCreationActions.SET_SCHEDULING_MAX_END_DATE:
            let newState = { ...state }
            newState.schedulingMaxEndDate = action.value
            return {
                ...newState,
            }
        case NewActivityCreationActions.SET_SMART_FILTER:
            return {
                ...state,
                smartFilter: action.value,
            }
        case NewActivityCreationActions.SET_VISIT_SELECTED_SURVEYS:
            return {
                ...state,
                visitActivity: {
                    ...state.visitActivity,
                    selectedSurveys: action.payload,
                },
            }
        case NewActivityCreationActions.SET_VISIT_SURVEY_LIST:
            return {
                ...state,
                visitActivity: {
                    ...state.visitActivity,
                    surveyList: action.payload,
                },
            }
        case NewActivityCreationActions.GET_VISIT_SURVEY_LIST_REQUEST:
            return {
                ...state,
                visitActivity: {
                    ...state.visitActivity,
                    surveyListLoading: true,
                },
            }
        case NewActivityCreationActions.GET_VISIT_SURVEY_LIST_FINALLY:
            return {
                ...state,
                visitActivity: {
                    ...state.visitActivity,
                    surveyListLoading: false,
                },
            }
        case NewActivityCreationActions.SET_VISIT_OVERLAP_TIME_DATA:
            return {
                ...state,
                visitOverlapTimeData: action.visitOverlapTimeData,
            }
        case NewActivityCreationActions.SET_IS_ACTIVITY_TYPE_STEP:
            return {
                ...state,
                isActivityTypeStep: action.payload,
            }
        case NewActivityCreationActions.SET_SELECTED_ACTIVITY_TYPE:
            return {
                ...state,
                selectedActivityType: action.payload,
            }
        case NewActivityCreationActions.SET_CURRENT_STEP:
            return {
                ...state,
                currentStep: action.payload,
            }
        default:
            return state
    }
}
